import clearRecipientListChangedFlagAction from '../actions/clearRecipientListChangedFlagAction';
import {
    addRecentlyRejectedSuggestionsTelemetry,
    clearRecentlyRejectedSuggestionsTelemetry,
    setCurrentlyShownSuggestion,
    addAllSuggestionsTelemetry,
    cacheSuggestion,
    clearSuggestionCache,
    modifyCurrentlyShowSuggestionInEditor,
} from '../actions/smartComposePluginActions';
import setTabPersonalizationSuggestion from '../actions/setTabPersonalizationSuggestion';
import updateRecipientsForSmartCompose from '../actions/updateRecipientsForSmartCompose';
import store from '../store/store';
import { mutator, mutatorAction } from 'satcheljs';
import { trace } from 'owa-trace';
import { CONTEXT_LENGTH, MAX_CACHE_SIZE } from '../store/schema/CachedSuggestion';
import getLastNCharacters from '../utils/getLastNCharacters';

export const updateRecipientsForSmartComposeMutator = mutator(
    updateRecipientsForSmartCompose,
    actionMessage => {
        store().viewState.recipientNames = actionMessage.recipients.map(
            recipient => recipient.persona.EmailAddress
        );
        store().viewState.recipientListChanged = true;
    }
);

export const clearRecipientListChangedFlagMutator = mutator(
    clearRecipientListChangedFlagAction,
    actionMessage => {
        store().viewState.recipientListChanged = false;
    }
);

export const setCurrentlyShownSuggestionMutator = mutator(
    setCurrentlyShownSuggestion,
    actionMessage => {
        store().viewState.currentlyShownSuggestion = actionMessage.suggestion;
        store().viewState.currentlyAnnouncedSuggestion = actionMessage.suggestion?.suggestionText;
    }
);

export const addRecentlyRejectedSuggestionsTelemetryMutator = mutator(
    addRecentlyRejectedSuggestionsTelemetry,
    actionMessage => {
        store().recentlyRejectedSuggestionsTelemetry.push(actionMessage.suggestionTelemetry);
    }
);

export const clearRecentlyRejectedSuggestionsTelemetryMutator = mutator(
    clearRecentlyRejectedSuggestionsTelemetry,
    actionMessage => {
        store().recentlyRejectedSuggestionsTelemetry = [];
    }
);

export const addAllSuggestionsTelemetryMutator = mutator(
    addAllSuggestionsTelemetry,
    actionMessage => {
        store().allSuggestionsTelemetry.push(actionMessage.suggestionTelemetry);
    }
);

export const setTabPersonalizationSuggestionMutator = mutator(
    setTabPersonalizationSuggestion,
    actionMessage => {
        store().personalizationSuggestions.tabHintSuggestion.prefer =
            actionMessage.tabPersonalizationSuggestion;
    }
);

export const cacheSuggestionMutator = mutator(cacheSuggestion, actionMessage => {
    const { context, suggestion, suggestionTelemetry } = actionMessage;
    const trimmedContext = getLastNCharacters(context, CONTEXT_LENGTH);
    trace.info(
        `[SmartComposePlugin]. Caching new suggestion. Context: '${trimmedContext}'. Suggestion: '${suggestion}'`
    );
    const { suggestionCache } = store();
    suggestionCache.push({
        context: trimmedContext,
        suggestion: suggestion,
        suggestionTelemetry: suggestionTelemetry,
    });

    if (suggestionCache.length > MAX_CACHE_SIZE) {
        suggestionCache.shift();
    }
});

export const clearSuggestionCacheMutator = mutator(clearSuggestionCache, actionMessage => {
    store().suggestionCache = [];
});

export const modifyCurrentlyShowSuggestionInEditorMutator = mutator(
    modifyCurrentlyShowSuggestionInEditor,
    actionMessage => {
        const { currentlyShownSuggestion } = store().viewState;
        currentlyShownSuggestion.suggestionText = actionMessage.newText;

        const newTextNode = document.createTextNode(actionMessage.newText);

        if (
            currentlyShownSuggestion.suggestionNode?.suggestionTextNode?.parentNode ===
            currentlyShownSuggestion.suggestionNode?.suggestionNode
        ) {
            currentlyShownSuggestion.suggestionNode?.suggestionNode?.replaceChild(
                newTextNode,
                currentlyShownSuggestion.suggestionNode?.suggestionTextNode
            );
            currentlyShownSuggestion.suggestionNode.suggestionTextNode = newTextNode;
        }
    }
);

export const removeFirstCharacterFromSuggestion = mutatorAction(
    'removeFirstCharacterFromSuggestion',
    () => {
        const { currentlyShownSuggestion } = store().viewState;
        if (currentlyShownSuggestion.suggestionText.length > 1) {
            const trimmedSuggestion = currentlyShownSuggestion.suggestionText.substring(1);

            // trim the suggestion string
            currentlyShownSuggestion.suggestionText = trimmedSuggestion;

            // trim the suggestion node
            currentlyShownSuggestion.suggestionNode.suggestionTextNode.nodeValue = trimmedSuggestion;
        } else if (currentlyShownSuggestion.suggestionText.length === 1) {
            currentlyShownSuggestion.suggestionText = '';
            currentlyShownSuggestion.suggestionNode.suggestionTextNode.nodeValue = '';
        }
    }
);
